import React, { useState } from 'react';
import { Upload, Loader, ExternalLink, CheckCircle, AlertCircle, Info, ArrowRight, Coins } from 'lucide-react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';

const MeteoraPoolForm = ({ userWallet, onClose, onSuccess }) => {
  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  
  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(1); // 1: Token Creation, 2: Pool Creation, 3: Success
  const [createdToken, setCreatedToken] = useState(null);
  
  // Token creation form data
  const [tokenData, setTokenData] = useState({
    name: '',
    symbol: '',
    description: '',
    supply: 1000000000, // 1 billion tokens default
    logoData: null,
    logoFilename: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: ''
  });
  
  // Pool creation form data
  const [poolData, setPoolData] = useState({
    tokenLiquidityAmount: 500000000, // 500M tokens default
    solLiquidityAmount: 5.0, // 5 SOL default
    tradingFee: 0.25, // 0.25% default
    activationDelay: 5 // 5 minutes default
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleTokenInputChange = (e) => {
    const { name, value } = e.target;
    setTokenData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePoolInputChange = (e) => {
    const { name, value } = e.target;
    setPoolData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        setError('Logo file must be less than 500KB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (PNG, JPG, or GIF)');
        return;
      }
      
      setError(null);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setTokenData(prev => ({
          ...prev,
          logoData: base64,
          logoFilename: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateTokenCreationCost = () => {
    return 0.002 + 0.001; // Solana fees + metadata fees
  };

  const calculatePoolCreationCost = () => {
    const meteoraFee = 0.005;  // Updated to reflect new reduced fee
    const transactionFee = 0.00065;
    const liquiditySOL = parseFloat(poolData.solLiquidityAmount);
    return meteoraFee + transactionFee + liquiditySOL;
  };

  // Step 1: Create SPL Token
  const handleCreateToken = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!tokenData.name || !tokenData.symbol || !tokenData.description) {
        throw new Error('Please fill in all required token fields');
      }

      if (!publicKey) {
        throw new Error('Please connect your Solana wallet first');
      }

      const payload = {
        name: tokenData.name,
        symbol: tokenData.symbol.toUpperCase(),
        description: tokenData.description,
        platform: 'spl_token',
        initial_supply: parseInt(tokenData.supply),
        website: tokenData.website,
        twitter: tokenData.twitter,
        telegram: tokenData.telegram,
        discord: tokenData.discord,
        user_wallet: publicKey.toString(),
        ...(tokenData.logoData && {
          logo_data: tokenData.logoData,
          logo_filename: tokenData.logoFilename
        })
      };

      console.log('🪙 Creating SPL token for Meteora pool:', payload);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/create-spl-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Token creation failed: ${response.status}`);
      }

      const tokenResult = await response.json();
      
      if (tokenResult.success) {
        setCreatedToken(tokenResult);
        setCurrentStep(2); // Move to pool creation step
        console.log('✅ SPL token created successfully:', tokenResult);
      } else {
        throw new Error(tokenResult.message || 'Token creation failed');
      }

    } catch (err) {
      console.error('❌ Token creation failed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Create Meteora Pool
  const handleCreatePool = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!createdToken) {
        throw new Error('Token must be created first');
      }

      if (parseFloat(poolData.solLiquidityAmount) < 0.1) {
        throw new Error('Minimum SOL liquidity is 0.1 SOL');
      }

      const payload = {
        base_token_address: createdToken.mint_address,
        quote_token_address: 'So11111111111111111111111111111111111111112', // SOL
        base_token_amount: parseInt(poolData.tokenLiquidityAmount),
        quote_token_amount: parseFloat(poolData.solLiquidityAmount),
        trading_fee: parseFloat(poolData.tradingFee),
        activation_delay_minutes: parseInt(poolData.activationDelay),
        platform: 'meteora',
        user_wallet: publicKey.toString(),
        token_metadata: {
          name: tokenData.name,
          symbol: tokenData.symbol,
          description: tokenData.description
        }
      };

      console.log('⚡ Creating Meteora DAMM v2 pool:', payload);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/create-meteora-pool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Pool creation failed: ${response.status}`);
      }

      const poolResult = await response.json();
      
      if (poolResult.success) {
        setResult({
          token: createdToken,
          pool: poolResult,
          totalCost: calculateTokenCreationCost() + calculatePoolCreationCost()
        });
        setCurrentStep(3); // Move to success step
        console.log('✅ Meteora pool created successfully:', poolResult);
      } else {
        throw new Error(poolResult.message || 'Pool creation failed');
      }

    } catch (err) {
      console.error('❌ Pool creation failed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Token Creation Form
  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-4 mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <span className="text-blue-400 font-medium">Create Token</span>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center text-sm">2</div>
            <span className="text-gray-500">Create Pool</span>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center text-sm">3</div>
            <span className="text-gray-500">Launch</span>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Coins className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-400">Step 1: Create Your SPL Token</h3>
              <p className="text-sm text-blue-300 mt-1">
                First, we'll create your SPL token with full metadata. Then we'll create a Meteora DAMM v2 pool for trading.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-400">Error</h3>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleCreateToken} className="space-y-6">
          {/* Token Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">🪙 Token Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Token Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={tokenData.name}
                  onChange={handleTokenInputChange}
                  placeholder="e.g., My Awesome Token"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Token Symbol *
                </label>
                <input
                  type="text"
                  name="symbol"
                  value={tokenData.symbol}
                  onChange={handleTokenInputChange}
                  placeholder="e.g., MAT"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  maxLength="10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={tokenData.description}
                onChange={handleTokenInputChange}
                placeholder="Describe your token and its purpose..."
                rows="3"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Total Supply
              </label>
              <input
                type="number"
                name="supply"
                value={tokenData.supply}
                onChange={handleTokenInputChange}
                min="1000000"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Total number of tokens to create (you'll provide some as liquidity)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Token Logo (Optional)
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="logoUpload"
                />
                <label
                  htmlFor="logoUpload"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 cursor-pointer hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <Upload className="h-4 w-4" />
                  <span>{tokenData.logoFilename || 'Choose logo file (PNG, JPG, GIF - max 500KB)'}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">🌐 Social Links (Optional)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={tokenData.website}
                  onChange={handleTokenInputChange}
                  placeholder="https://yourwebsite.com"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Twitter
                </label>
                <input
                  type="text"
                  name="twitter"
                  value={tokenData.twitter}
                  onChange={handleTokenInputChange}
                  placeholder="@yourhandle"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Telegram
                </label>
                <input
                  type="text"
                  name="telegram"
                  value={tokenData.telegram}
                  onChange={handleTokenInputChange}
                  placeholder="@yourgroup"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Discord
                </label>
                <input
                  type="text"
                  name="discord"
                  value={tokenData.discord}
                  onChange={handleTokenInputChange}
                  placeholder="discord.gg/yourinvite"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">💰 Token Creation Cost</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Solana Account Creation:</span>
                <span className="text-white">0.002 SOL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Metadata Storage:</span>
                <span className="text-white">0.001 SOL</span>
              </div>
              <div className="border-t border-gray-700 pt-2">
                <div className="flex justify-between font-medium">
                  <span className="text-gray-300">Step 1 Total:</span>
                  <span className="text-white">{calculateTokenCreationCost().toFixed(5)} SOL</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !publicKey}
            className="w-full p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>Creating Token...</span>
              </>
            ) : (
              <>
                <span>🪙 Create SPL Token</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>

          {!publicKey && (
            <p className="text-center text-sm text-yellow-400">
              ⚠️ Please connect your Solana wallet to create a token
            </p>
          )}
        </form>
      </div>
    );
  }

  // Step 2: Pool Creation Form
  if (currentStep === 2 && createdToken) {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-4 mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm">✓</div>
            <span className="text-green-400 font-medium">Token Created</span>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
            <span className="text-blue-400 font-medium">Create Pool</span>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center text-sm">3</div>
            <span className="text-gray-500">Launch</span>
          </div>
        </div>

        {/* Token Created Success */}
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-green-400">Token Created Successfully!</h3>
              <p className="text-sm text-green-300 mt-1">
                <strong>{tokenData.symbol}</strong> ({tokenData.name}) has been created. Now let's create your Meteora pool.
              </p>
              <p className="text-xs text-green-400 mt-1 font-mono">
                Mint: {createdToken.mint_address}
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-400">Step 2: Create Meteora DAMM v2 Pool</h3>
              <p className="text-sm text-blue-300 mt-1">
                Provide initial liquidity with both your tokens and SOL to create a trading pool on Meteora.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-400">Error</h3>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleCreatePool} className="space-y-6">
          {/* Liquidity Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">⚡ Initial Liquidity</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Tokens ({tokenData.symbol})
                </label>
                <input
                  type="number"
                  name="tokenLiquidityAmount"
                  value={poolData.tokenLiquidityAmount}
                  onChange={handlePoolInputChange}
                  min="1000000"
                  max={tokenData.supply}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Amount of {tokenData.symbol} tokens to add as liquidity
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SOL Amount
                </label>
                <input
                  type="number"
                  name="solLiquidityAmount"
                  value={poolData.solLiquidityAmount}
                  onChange={handlePoolInputChange}
                  step="0.1"
                  min="0.1"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Amount of SOL to add as liquidity (minimum 0.1 SOL)
                </p>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-sm text-gray-300">
                <strong>Initial Price:</strong> 1 {tokenData.symbol} = {(parseFloat(poolData.solLiquidityAmount) / parseFloat(poolData.tokenLiquidityAmount)).toExponential(3)} SOL
              </p>
            </div>
          </div>

          {/* Pool Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">⚙️ Pool Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Trading Fee (%)
                </label>
                <select
                  name="tradingFee"
                  value={poolData.tradingFee}
                  onChange={handlePoolInputChange}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value={0.01}>0.01% (Very Low)</option>
                  <option value={0.05}>0.05% (Low)</option>
                  <option value={0.25}>0.25% (Standard)</option>
                  <option value={0.5}>0.5% (Medium)</option>
                  <option value={1.0}>1.0% (High)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Activation Delay (minutes)
                </label>
                <input
                  type="number"
                  name="activationDelay"
                  value={poolData.activationDelay}
                  onChange={handlePoolInputChange}
                  min="1"
                  max="1440"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Total Cost Breakdown */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">💰 Total Launch Cost</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-green-400">
                <span>Step 1 - Token Creation:</span>
                <span>{calculateTokenCreationCost().toFixed(5)} SOL ✓</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Meteora Pool Fee:</span>
                <span className="text-white">0.005 SOL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Transaction Fee:</span>
                <span className="text-white">0.00065 SOL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">SOL Liquidity:</span>
                <span className="text-white">{poolData.solLiquidityAmount} SOL</span>
              </div>
              <div className="border-t border-gray-700 pt-2">
                <div className="flex justify-between font-medium">
                  <span className="text-gray-300">Total Cost:</span>
                  <span className="text-white">{(calculateTokenCreationCost() + calculatePoolCreationCost()).toFixed(5)} SOL</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !publicKey}
            className="w-full p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>Creating Pool...</span>
              </>
            ) : (
              <>
                <span>⚡ Create Meteora Pool</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </div>
    );
  }

  // Step 3: Success
  if (currentStep === 3 && result) {
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl">🎉</div>
        <div>
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Launch Complete!</h3>
          <p className="text-gray-300 mb-4">
            Your token <strong>{tokenData.symbol}</strong> and Meteora pool are now live and ready for trading!
          </p>
        </div>
        
        {/* Success Details */}
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-left">
          <h4 className="text-lg font-bold text-green-400 mb-3">🎯 Launch Summary</h4>
          <div className="space-y-2 text-sm text-gray-300">
            <div className="flex justify-between">
              <span>Token Name:</span>
              <span className="text-white">{tokenData.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Token Symbol:</span>
              <span className="text-white">{tokenData.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span>Token Address:</span>
              <span className="text-white font-mono text-xs">{result.token.mint_address}</span>
            </div>
            <div className="flex justify-between">
              <span>Pool Address:</span>
              <span className="text-white font-mono text-xs">{result.pool.pool_address}</span>
            </div>
            <div className="flex justify-between">
              <span>Initial Price:</span>
              <span className="text-white">1 {tokenData.symbol} = {(parseFloat(poolData.solLiquidityAmount) / parseFloat(poolData.tokenLiquidityAmount)).toExponential(3)} SOL</span>
            </div>
            <div className="flex justify-between">
              <span>Trading Fee:</span>
              <span className="text-white">{poolData.tradingFee}%</span>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          <a
            href={`https://app.meteora.ag/pools/${result.pool.pool_address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 w-full p-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <span>⚡ View Pool on Meteora</span>
            <ExternalLink className="h-4 w-4" />
          </a>
          
          <a
            href={`https://solscan.io/token/${result.token.mint_address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 w-full p-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <span>🔍 View Token on Solscan</span>
            <ExternalLink className="h-4 w-4" />
          </a>
          
          <button
            onClick={() => {
              onSuccess && onSuccess(result);
              onClose();
            }}
            className="w-full p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            ✨ Launch Another Token
          </button>
        </div>
        
        <div className="text-xs text-gray-400 bg-gray-800 p-3 rounded-lg text-left">
          <p><strong>💰 Total Cost Breakdown:</strong></p>
          <p>• Token Creation: {calculateTokenCreationCost().toFixed(5)} SOL</p>
          <p>• Pool Creation: {(calculatePoolCreationCost() - parseFloat(poolData.solLiquidityAmount)).toFixed(5)} SOL</p>
          <p>• Initial Liquidity: {poolData.solLiquidityAmount} SOL</p>
          <p>• <strong>Total Paid: {result.totalCost.toFixed(5)} SOL</strong></p>
          <p>• Platform: Meteora DAMM v2 (via TokenOS)</p>
        </div>
      </div>
    );
  }

  return null;
};

export default MeteoraPoolForm;
